import { useMemo, useRef } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import CustomShaderMaterial from 'three-custom-shader-material';
import { mergeVertices } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import { vertexShader, fragmentShader } from './shaders';
import { BLOB_CONFIG } from './blobConfig';

const LERP_SPEED = 2.0; // seconds for color transition

export function Blob({
  gradientColor1,
  gradientColor2,
  gradientColor3,
  config = BLOB_CONFIG,
  onClick,
  animate = true,
  /** Icosahedron subdivision detail; higher = smoother but heavier (default matches historical look). */
  icosahedronDetail = 100,
}) {
  const targetColor1 = useRef(new THREE.Color(gradientColor1));
  const targetColor2 = useRef(new THREE.Color(gradientColor2));
  const targetColor3 = useRef(new THREE.Color(gradientColor3));

  // Update targets when props change
  targetColor1.current.set(gradientColor1);
  targetColor2.current.set(gradientColor2);
  targetColor3.current.set(gradientColor3);

  const geometry = useMemo(() => {
    const geo = mergeVertices(new THREE.IcosahedronGeometry(1, icosahedronDetail));
    geo.computeTangents();
    return geo;
  }, [icosahedronDetail]);

  const uniforms = useMemo(
    () => ({
      uTime: { value: 0 },
      uPositionFrequency: { value: config.uPositionFrequency },
      uPositionStrength: { value: config.uPositionStrength },
      uTimeFrequency: { value: config.uTimeFrequency },
      uSmallWavePositionFrequency: { value: config.uSmallWavePositionFrequency },
      uSmallWavePositionStrength: { value: config.uSmallWavePositionStrength },
      uSmallWaveTimeFrequency: { value: config.uSmallWaveTimeFrequency },
      uUseCustomGradient: { value: 1.0 },
      uGradientColor1: { value: new THREE.Color(gradientColor1) },
      uGradientColor2: { value: new THREE.Color(gradientColor2) },
      uGradientColor3: { value: new THREE.Color(gradientColor3) },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  useFrame((_, delta) => {
    if (!animate) return;

    // Wall-clock time: keeps deforming when R3F/THREE clock pauses (e.g. demand frameloop, mobile focus).
    uniforms.uTime.value = performance.now() * 0.001;

    // Smooth color lerp
    const lerpFactor = 1.0 - Math.pow(0.001, delta / LERP_SPEED);
    uniforms.uGradientColor1.value.lerp(targetColor1.current, lerpFactor);
    uniforms.uGradientColor2.value.lerp(targetColor2.current, lerpFactor);
    uniforms.uGradientColor3.value.lerp(targetColor3.current, lerpFactor);
  });

  return (
    <mesh
      geometry={geometry}
      onClick={onClick ? (e) => {
        e.stopPropagation();
        onClick();
      } : undefined}
      onPointerOver={onClick ? () => {
        if (document.body) document.body.style.cursor = 'pointer';
      } : undefined}
      onPointerOut={onClick ? () => {
        if (document.body) document.body.style.cursor = 'default';
      } : undefined}
    >
      <CustomShaderMaterial
        key={String(config.flatShading)}
        baseMaterial={THREE.MeshPhysicalMaterial}
        vertexShader={vertexShader}
        fragmentShader={fragmentShader}
        uniforms={uniforms}
        roughness={config.roughness}
        metalness={config.metalness}
        envMapIntensity={config.envMapIntensity}
        clearcoat={config.clearcoat}
        clearcoatRoughness={config.clearcoatRoughness}
        transmission={config.transmission}
        wireframe={config.wireframe}
        flatShading={config.flatShading}
      />
    </mesh>
  );
}
